/// <amd-module name="Test/TestPlansHubRefresh/TestPlansSegment/Redux/TestPlansSegmentSaga" />
import { Favorite, FavoriteCreateParameters } from "Favorites/Common/Contracts/Favorite";
import { IFavoriteRestClient } from "Favorites/Common/RestClient/Favorite";
import * as React from "react";
import { Channel, channel, effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { TestPlan } from "TestPlanCommon/Contracts/Test";
import { ITestHubDataService } from "Test/TestPlansHubRefresh/Service/TestHubDataService";
import { TelemetryConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";
import { TestHubRefreshActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshActions";
import { isAdvancedLicenseEnabled } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import * as Resources from "Test/TestPlansHubRefresh/TestPlansSegment/Resources";
import {
    FavoriteConstants,
    ProgressReportConstants,
    WorkItemFieldConstants
} from "Test/TestPlansHubRefresh/TestPlansSegment/TestPlansSegmentConstants";
import { ITestPlanProgressSummary, TestPlanResultsSource } from "Test/TestPlansHubRefresh/TestPlansSegment/Utils/TestPlanResultsSource";
import { ITfsPageService } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { ILegacyComponentProperties, LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLocationService } from "VSS/Platform/Location";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { TestPlansSegmentActions, TestPlansSegmentActionsTypes } from "./TestPlansSegmentActions";
import { errorHandledSaga } from "Test/TestPlansHubRefresh/TestHub/Util";

interface IUpdateTestPlanPayload {
    planId: number;
    testPlanTitle: string;
    testPlanStartDate: Date;
    testPlanFinishDate: Date;
    testPlanAreaPath: string;
    testPlanIterationPath: string;
}

export function* TestPlansSegmentSaga(): SagaIterator {
    yield effects.takeLatest(TestPlansSegmentActionsTypes.getInitialFavoriteStatusForPlan, _getInitialFavoriteStatusForPlan);
    yield effects.takeLatest(TestPlansSegmentActionsTypes.getTestPlanProgressSummary, _getTestPlanProgressSummary);
    yield effects.takeLatest(TestPlansSegmentActionsTypes.openProgressReport, _openProgressReport);
    yield effects.takeLatest(TestPlansSegmentActionsTypes.onToggleFavorite, _onToggleFavorite);
    yield effects.takeLatest(TestPlansSegmentActionsTypes.fetchFavorites, _fetchFavorites);
    yield effects.takeLatest(TestPlansSegmentActionsTypes.changeTestPlan, _changeTestPlan);
    yield effects.takeLatest(TestPlansSegmentActionsTypes.editTestPlan, _editTestPlan);
    yield effects.takeLatest(TestPlansSegmentActionsTypes.openTestPlanSettingsDialog, _openTestPlanSettingsDialog);
}

export function* getInitialFavoriteStatusForPlan(
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.getInitialFavoriteStatusForPlan>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const favoriteClient: IFavoriteRestClient = (yield effects.call([pageContext, pageContext.getRestClient], "IFavoriteRestClient")) as any;
    let artifactId: string = String(action.payload.planId);
    const favorite: Favorite = (yield effects.call(
        [favoriteClient, favoriteClient.getFavoriteByArtifact],
        FavoriteConstants.ArtifactType,
        artifactId,
        FavoriteConstants.ArtifactScopeType
    )) as any;
    let favoriteState = false;
    let favoriteId = undefined;
    if (favorite) {
        favoriteState = true;
        favoriteId = favorite.id;
    }
    yield effects.put(TestPlansSegmentActions.updateFavoriteStatus(favoriteState, favoriteId));
}

export function* getTestPlanProgressSummary(
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.getTestPlanProgressSummary>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    let testPlanProgressSummaryText = "";
    let planId: number = action.payload.planId;

    try {
        if (isFeatureFlagEnabled(pageContext, ProgressReportConstants.EnableNewAnalyticsHubForManualTestFF, false)) {
            let testPlanResultsSource: TestPlanResultsSource = new TestPlanResultsSource(
                ProgressReportConstants.TestResultsReportSummaryCommandName,
                pageContext
            );
            let testPlanProgressSummary: ITestPlanProgressSummary = (yield effects.call(
                [testPlanResultsSource, testPlanResultsSource.getTestPlanProgressSummary],
                planId
            )) as any;

            if (testPlanProgressSummary.TotalCount) {
                if (testPlanProgressSummary.ExecPct === 0) {
                    testPlanProgressSummaryText = format(Resources.ProgressReportRun, testPlanProgressSummary.ExecPct);
                } else {
                    testPlanProgressSummaryText = format(
                        Resources.ProgressReportRunPassed,
                        testPlanProgressSummary.ExecPct,
                        testPlanProgressSummary.PassedPct
                    );
                }
            }
        }
    } catch (e) {
        const telemetryPayload = {
            planId: planId,
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled),
            message: e.message,
            stack: e.stack,
            error: e
        };

        yield effects.put(
            TelemetryActions.publishTelemetry(TelemetryConstants.NAVIGATE_TO_PROGRESS_REPORT_FROM_TEST_HUB_REFRESH_ERROR, telemetryPayload)
        );
    }

    yield effects.put(TestPlansSegmentActions.updateTestPlanProgressSummary(testPlanProgressSummaryText));
}

/**
 * opens test plan progress report page.
 */
export function* openProgressReport(action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.openProgressReport>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    let projectName = "";
    if (tfsPageData && tfsPageData.project) {
        projectName = tfsPageData.project.name;
    }

    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
    const routeValues: { [key: string]: string } = {
        project: projectName
    };
    // constructing progress report url from route id.
    let progressReportUrl = (yield effects.call(
        [locationService, locationService.routeUrl],
        ProgressReportConstants.ManualTestAnalyticsHubRouteId,
        routeValues
    )) as any;

    const newWindow = (yield effects.call([window, window.open], progressReportUrl, "_blank")) as any;
    if (newWindow) {
        newWindow.opener = null;
    }

    let planId: number = action.payload.planId;

    const telemetryPayload = {
        planId: planId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };

    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.NAVIGATE_TO_PROGRESS_REPORT_FROM_TEST_HUB_REFRESH, telemetryPayload));
}

export function* onToggleFavorite(action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.onToggleFavorite>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    const favoriteClient: IFavoriteRestClient = (yield effects.call([pageContext, pageContext.getRestClient], "IFavoriteRestClient")) as any;
    let favoriteState = FavoriteConstants.MarkFavorite;
    if (action.payload.favoriteStatus) {
        yield effects.call(
            [favoriteClient, favoriteClient.deleteFavoriteById],
            action.payload.favoriteId,
            FavoriteConstants.ArtifactType,
            FavoriteConstants.ArtifactScopeType,
            tfsPageData.project.id
        );
        yield effects.put(TestPlansSegmentActions.updateFavoriteStatus(!action.payload.favoriteStatus));
        favoriteState = FavoriteConstants.RemoveFavorite;
    } else {
        const favoriteParameters: FavoriteCreateParameters = {
            artifactId: String(action.payload.testPlanId),
            artifactProperties: {},
            artifactName: action.payload.testPlanName,
            artifactType: FavoriteConstants.ArtifactType,
            artifactScope: {
                id: tfsPageData.project.id,
                type: FavoriteConstants.ArtifactScopeType,
                name: tfsPageData.project.name
            }
        };
        const favorite: Favorite = (yield effects.call([favoriteClient, favoriteClient.createFavorite], favoriteParameters)) as any;
        yield effects.put(TestPlansSegmentActions.updateFavoriteStatus(!action.payload.favoriteStatus, favorite.id));
    }
    const telemetryPayload = {
        favoriteState: favoriteState,
        planId: action.payload.testPlanId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.TOGGLE_FAVORITE, telemetryPayload));
}

export function* fetchFavorites(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    const favoriteClient: IFavoriteRestClient = (yield effects.call([pageContext, pageContext.getRestClient], "IFavoriteRestClient")) as any;
    const favoritesData: Favorite[] = (yield effects.call(
        [favoriteClient, favoriteClient.getFavorites],
        FavoriteConstants.ArtifactType,
        FavoriteConstants.ArtifactScopeType,
        tfsPageData.project.id,
        true
    )) as any;
    let favoriteCount = 0;
    if (favoritesData) {
        let favorites: Favorite[] = [];
        for (let i = 0; i < favoritesData.length; i++) {
            if (!favoritesData[i].artifactIsDeleted) {
                favorites.push(favoritesData[i]);
            }
        }
        yield effects.put(TestPlansSegmentActions.updateFavoritePlansList(favorites));
        favoriteCount = favorites.length;
    }
    const telemetryPayload = {
        favoriteCount: favoriteCount,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.FETCH_FAVORITE, telemetryPayload));
}

export function* changeTestPlan(action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.changeTestPlan>): SagaIterator {
    yield effects.put(SuitesActions.updateLoadingStatus(true));
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    yield effects.put(SuitesActions.reloadTestPlanAndSuites(action.payload.planId));
    yield effects.put(SuitesActions.updateLoadingStatus(false));

    const favoriteClient: IFavoriteRestClient = (yield effects.call([pageContext, pageContext.getRestClient], "IFavoriteRestClient")) as any;
    const favorite: Favorite = (yield effects.call(
        [favoriteClient, favoriteClient.getFavoriteByArtifact],
        FavoriteConstants.ArtifactType,
        action.payload.planId.toString(),
        FavoriteConstants.ArtifactScopeType
    )) as any;
    yield effects.put(TestPlansSegmentActions.updateFavoriteStatus(true, favorite.id));

    let registryEntries = {
        SelectedPlanId: action.payload.planId.toString()
    };
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
    const telemetryPayload = {
        newPlanId: action.payload.planId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.CHANGE_TEST_PLAN, telemetryPayload));
}

export function* editTestPlan(action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.editTestPlan>): SagaIterator {
    const updateTestPlanChannel: Channel<IUpdateTestPlanPayload> = (yield effects.call(channel)) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;
    yield effects.call(
        [contributionService, contributionService.getContributionsAsync],
        ["ms.vss-features.ui-responsive-viewport-content", "ms.vss-work-web.common-content"]
    );

    const workItemService: IWorkItemService = (yield effects.call([pageContext, pageContext.getService], "work-item-service")) as any;
    if (workItemService) {
        yield effects.call([workItemService, workItemService.openWorkItem], action.payload.planId, {
            save: (workItem: any): void => {
                const newTestPlanTitle = workItem.getTitle();
                const newTestPlanStartDate = workItem.getFieldValue(WorkItemFieldConstants.StartDate);
                const newTestPlanFinishDate = workItem.getFieldValue(WorkItemFieldConstants.FinishDate);
                const newTestPlanAreaPath = workItem.getFieldValue(WorkItemFieldConstants.AreaPath);
                const newTestPlanIterationPath = workItem.getFieldValue(WorkItemFieldConstants.IterationPath);

                updateTestPlanChannel.put({
                    planId: action.payload.planId,
                    testPlanTitle: newTestPlanTitle,
                    testPlanStartDate: newTestPlanStartDate,
                    testPlanFinishDate: newTestPlanFinishDate,
                    testPlanAreaPath: newTestPlanAreaPath,
                    testPlanIterationPath: newTestPlanIterationPath
                });
            }
        });
    }

    const payload: IUpdateTestPlanPayload = (yield effects.take(updateTestPlanChannel)) as any;
    yield effects.put(
        TestHubRefreshActions.updateTestPlan(
            payload.planId,
            payload.testPlanTitle,
            payload.testPlanStartDate,
            payload.testPlanFinishDate,
            payload.testPlanAreaPath,
            payload.testPlanIterationPath
        )
    );
}

export function* openTestPlanSettingsDialog(
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.openTestPlanSettingsDialog>
): SagaIterator {
    const testPlanSettingsChannel: Channel<any> = (yield effects.call(channel)) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    const testPlan: TestPlan = (yield effects.call([dataService, dataService.getTestPlanById], action.payload.planId)) as any;
    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;

    let onSaveTelemetryPayload = {};
    const testPlanSettingProps = {
        selectedPlan: testPlan,
        onSaveCallback: (testPlan: TestPlan) => {
            onTestPlanSettingsSave(testPlan, testPlanSettingsChannel);
            onSaveTelemetryPayload = {
                planId: testPlan.id,
                testPlanName: testPlan.name,
                buildDefinition: testPlan.buildDefinition,
                releaseDefinition: testPlan.releaseEnvironmentDefinition,
                isAdvancedLicenseEnabled: effects.select(isAdvancedLicenseEnabled)
            };
            return effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.CHANGE_TEST_PLAN_SETTINGS, onSaveTelemetryPayload));
        }
    };

    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/TestPlanSettingsDialogProxy"],
            dependentContributionIds: [
                "ms.vss-releaseManagement-web.release-service-data-external",
                "ms.vss-releaseManagement-web.release-definitions-exists-data-provider"
            ],
            wrappedType: "testplan-settings-dialog-proxy",
            wrappedProps: testPlanSettingProps
        };
        return React.createElement(LegacyComponent, properties);
    });
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));

    const payload = (yield effects.take(testPlanSettingsChannel)) as any;
    yield effects.put(TestHubRefreshActions.updateSelectedBuild(payload.selectedBuildId, payload.selectedBuildDefnId));
    yield effects.put(TestHubRefreshActions.updateSelectedRelease(payload.selectedReleaseId, payload.selectedReleaseEnvironmentId));
    const onClickTelemetryPayload = {
        planId: action.payload.planId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.CLICK_TEST_PLAN_SETTINGS, onClickTelemetryPayload));
}

function onTestPlanSettingsSave(testPlan: any, testPlanSettingsChannel: Channel<any>) {
    let selectedBuildId: string = "0";
    let selectedReleaseId: number = 0;
    let selectedReleaseEnvironmentId: number = 0;
    let selectedBuildDefnId: string = "0";
    if (testPlan.buildDefinition && testPlan.buildDefinition.id) {
        selectedBuildDefnId = testPlan.buildDefinition.id;
    }
    if (testPlan.build && testPlan.build.id) {
        selectedBuildId = testPlan.build.id;
    }
    if (testPlan.releaseEnvironmentDefinition && testPlan.releaseEnvironmentDefinition.definitionId) {
        selectedReleaseId = testPlan.releaseEnvironmentDefinition.definitionId;
    }
    if (testPlan.releaseEnvironmentDefinition && testPlan.releaseEnvironmentDefinition.environmentDefinitionId) {
        selectedReleaseEnvironmentId = testPlan.releaseEnvironmentDefinition.environmentDefinitionId;
    }
    testPlanSettingsChannel.put({
        selectedBuildId: selectedBuildId,
        selectedReleaseId: selectedReleaseId,
        selectedReleaseEnvironmentId: selectedReleaseEnvironmentId,
        selectedBuildDefnId: selectedBuildDefnId
    });
}

const _getInitialFavoriteStatusForPlan = errorHandledSaga(function* (
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.getInitialFavoriteStatusForPlan>
): SagaIterator {
    yield effects.call(getInitialFavoriteStatusForPlan, action);
});

const _getTestPlanProgressSummary = errorHandledSaga(function* (
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.getTestPlanProgressSummary>
): SagaIterator {
    yield effects.call(getTestPlanProgressSummary, action);
});

const _openProgressReport = errorHandledSaga(function* (
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.openProgressReport>
): SagaIterator {
    yield effects.call(openProgressReport, action);
});

const _onToggleFavorite = errorHandledSaga(function* (
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.onToggleFavorite>
): SagaIterator {
    yield effects.call(onToggleFavorite, action);
});

const _fetchFavorites = errorHandledSaga(function* (
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.fetchFavorites>
): SagaIterator {
    yield effects.call(fetchFavorites, action);
});

const _changeTestPlan = errorHandledSaga(function* (
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.changeTestPlan>
): SagaIterator {
    yield effects.call(changeTestPlan, action);
});

const _editTestPlan = errorHandledSaga(function* (
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.editTestPlan>
): SagaIterator {
    yield effects.call(editTestPlan, action);
});

const _openTestPlanSettingsDialog = errorHandledSaga(function* (
    action: ActionsOfType<TestPlansSegmentActions, TestPlansSegmentActionsTypes.openTestPlanSettingsDialog>
): SagaIterator {
    yield effects.call(openTestPlanSettingsDialog, action);
});